import { SecurityHelper, classifyNetworkError } from './helpers.js';
import { formatMessageContent } from './message.js';
import { createProductCarousel, initializeCarousel } from './product.js';
import { consumeStream } from './streaming.js';
import { resolveErrorMessage } from './transport.js';
import { renderLeadForm } from './lead-form.js';
import { renderBlocks } from './blocks/index.js';
import {
  MESSAGE_SEGMENT_DELAY,
  CAROUSEL_INIT_DELAY,
  MAX_SEGMENT_COUNT,
  MIN_SEGMENT_COUNT,
  CHARS_PER_SEGMENT,
  STREAM_INACTIVITY_TIMEOUT_MS
} from './constants.js';

/**
 * Renderer factory. Holds references to DOM and dependencies that the two
 * display flows ("animated paint" and "incremental streaming") share. Pulled
 * out of widget.js to keep that orchestrator under ~400 lines.
 */
export function createMessageRenderer({
  el,
  chatMessages,
  config,
  t,
  productParser,
  historyManager,
  removeTyping,
  markPendingUserMessagesAsSeen,
  createMessageMeta,
  incrementUnread
}) {
  function makeBotRow() {
    const row = document.createElement('div');
    row.className = 'message-row message bot message-fade-in';

    const avatar = document.createElement('div');
    avatar.className = 'avatar';
    avatar.innerHTML = `<img src="${config.botAvatar}" alt="">`;

    const bubble = document.createElement('div');
    bubble.className = 'bubble';

    row.appendChild(avatar);
    row.appendChild(bubble);
    return { row, bubble };
  }

  function appendMeta(timestamp) {
    const metaEl = createMessageMeta('bot', timestamp, false);
    if (metaEl) chatMessages.appendChild(metaEl);
  }

  function processHTML(element) {
    let result = '';
    Array.from(element.childNodes).forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        result += node.textContent;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const tagName = node.tagName.toLowerCase();
        const attributes = Array.from(node.attributes)
          .map((attr) => ` ${attr.name}="${SecurityHelper.escapeAttribute(attr.value)}"`)
          .join('');

        if (['br', 'img', 'hr'].includes(tagName)) {
          result += `<${tagName}${attributes}>`;
        } else {
          result += `<${tagName}${attributes}>`;
          result += processHTML(node);
          result += `</${tagName}>`;
        }
      }
    });
    return result;
  }

  function splitIntoSegments(html, segmentSize) {
    const segments = [];
    let currentSegment = '';
    let accumulatedLength = 0;
    let inTag = false;
    for (let i = 0; i < html.length; i++) {
      const char = html[i];
      currentSegment += char;
      if (char === '<') inTag = true;
      if (char === '>') inTag = false;
      if (!inTag) accumulatedLength++;
      if (accumulatedLength >= segmentSize && !inTag && char !== '<' && html[i + 1] !== '<') {
        segments.push(currentSegment);
        currentSegment = '';
        accumulatedLength = 0;
      }
    }
    if (currentSegment) segments.push(currentSegment);
    return segments;
  }

  async function displayBotResponseWithAnimation(content, isProductData = false) {
    removeTyping();
    markPendingUserMessagesAsSeen();

    let htmlContent = '';
    let carouselData = null;
    let products = null;

    if (isProductData && content.products && Array.isArray(content.products)) {
      products = content.products;
      const messageText = content.response || content.message || null;
      carouselData = createProductCarousel(products, messageText, t);
    } else {
      let textContent = '';
      if (typeof content === 'string') {
        textContent = content;
      } else if (content.response || content.message) {
        textContent = content.response || content.message;
      }
      if (textContent) {
        products = await productParser.parseProductsFromText(textContent);
        if (products && products.length > 0) {
          const cleanText = productParser.removeUrlsFromText(textContent);
          carouselData = createProductCarousel(products, cleanText || null, t);
        } else {
          htmlContent = SecurityHelper.sanitizeHTML(formatMessageContent(textContent));
        }
      }
    }

    if (carouselData) {
      htmlContent = carouselData.html;

      const { row, bubble } = makeBotRow();
      bubble.style.padding = '0 !important';
      bubble.style.maxWidth = '100% !important';
      bubble.style.background = 'transparent !important';
      bubble.style.border = 'none !important';
      bubble.innerHTML = htmlContent;
      chatMessages.appendChild(row);

      const botTimestamp = new Date().toISOString();
      appendMeta(botTimestamp);
      chatMessages.scrollTop = chatMessages.scrollHeight;

      setTimeout(() => initializeCarousel(carouselData.carouselId, el), CAROUSEL_INIT_DELAY);

      historyManager.addMessage({ type: 'bot', content: htmlContent, timestamp: botTimestamp });
      incrementUnread();
      return;
    }

    if (!htmlContent) htmlContent = t.errorProcessing;

    const contentLength = htmlContent.length;
    const segmentCount = Math.min(
      MAX_SEGMENT_COUNT,
      Math.max(MIN_SEGMENT_COUNT, Math.ceil(contentLength / CHARS_PER_SEGMENT))
    );
    const segmentSize = Math.ceil(contentLength / segmentCount);

    const tempElement = document.createElement('div');
    tempElement.innerHTML = htmlContent;
    const processedHTML = processHTML(tempElement);

    const { row, bubble } = makeBotRow();
    bubble.innerHTML = '';
    chatMessages.appendChild(row);

    const botTimestamp = new Date().toISOString();
    appendMeta(botTimestamp);

    const segments = splitIntoSegments(processedHTML, segmentSize);
    let displayedContent = '';
    for (let i = 0; i < segments.length; i++) {
      displayedContent += segments[i];
      bubble.innerHTML = displayedContent;
      chatMessages.scrollTop = chatMessages.scrollHeight;
      if (i < segments.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, MESSAGE_SEGMENT_DELAY));
      }
    }

    historyManager.addMessage({ type: 'bot', content: htmlContent, timestamp: botTimestamp });
    incrementUnread();
  }

  async function displayBotResponseStreaming(response, signal) {
    markPendingUserMessagesAsSeen();

    let row = null;
    let bubble = null;
    let cursor = null;
    let botTimestamp = null;

    const ensureBubble = () => {
      if (row) return;
      removeTyping();
      ({ row, bubble } = makeBotRow());

      cursor = document.createElement('span');
      cursor.className = 'streaming-cursor';
      bubble.appendChild(cursor);

      chatMessages.appendChild(row);

      botTimestamp = new Date().toISOString();
      appendMeta(botTimestamp);
      chatMessages.scrollTop = chatMessages.scrollHeight;
    };

    const renderAccumulated = (accumulated) => {
      ensureBubble();
      const safeHtml = SecurityHelper.sanitizeHTML(formatMessageContent(accumulated));
      bubble.innerHTML = safeHtml;
      bubble.appendChild(cursor);
      chatMessages.scrollTop = chatMessages.scrollHeight;
    };

    let finalText = '';
    try {
      finalText = await consumeStream(
        response,
        { onChunk: (_chunk, accumulated) => renderAccumulated(accumulated) },
        { inactivityTimeoutMs: STREAM_INACTIVITY_TIMEOUT_MS, signal }
      );
    } catch (err) {
      if (err && err.name === 'AbortError') {
        if (row && row.parentNode) row.parentNode.removeChild(row);
        return;
      }
      console.error('Erreur pendant le streaming:', err);
      ensureBubble();
      if (cursor) cursor.remove();
      const code = classifyNetworkError(err, null);
      bubble.innerHTML = SecurityHelper.sanitizeHTML(formatMessageContent(resolveErrorMessage(t, code)));
      return;
    }

    if (!finalText.trim()) {
      ensureBubble();
      if (cursor) cursor.remove();
      bubble.innerHTML = SecurityHelper.sanitizeHTML(formatMessageContent(t.errorNoResponse));
      historyManager.addMessage({ type: 'bot', content: bubble.innerHTML, timestamp: botTimestamp });
      incrementUnread();
      return;
    }

    cursor.remove();

    let products = null;
    try {
      products = await productParser.parseProductsFromText(finalText);
    } catch (err) {
      console.error('Erreur lors du parsing des produits:', err);
    }

    if (products && products.length > 0) {
      const cleanText = productParser.removeUrlsFromText(finalText);
      const carouselData = createProductCarousel(products, cleanText || null, t);
      bubble.style.padding = '0';
      bubble.style.maxWidth = '100%';
      bubble.style.background = 'transparent';
      bubble.style.border = 'none';
      bubble.innerHTML = carouselData.html;
      setTimeout(() => initializeCarousel(carouselData.carouselId, el), CAROUSEL_INIT_DELAY);
      historyManager.addMessage({ type: 'bot', content: carouselData.html, timestamp: botTimestamp });
    } else {
      const finalHtml = SecurityHelper.sanitizeHTML(formatMessageContent(finalText));
      bubble.innerHTML = finalHtml;
      historyManager.addMessage({ type: 'bot', content: finalHtml, timestamp: botTimestamp });
    }

    chatMessages.scrollTop = chatMessages.scrollHeight;
    incrementUnread();
  }

  /**
   * Render an array of rich-display blocks (table, gallery, links,
   * property_carousel, ...) directly below the previous bot bubble. The row
   * has no avatar (a hidden spacer keeps the alignment) so the content can
   * span the full bubble width.
   *
   * The block *data* (not a DOM snapshot) is persisted to history so a page
   * reload can re-render the structured content with the current renderer.
   * Pass `{ save: false }` when replaying from history to avoid re-saving.
   */
  function displayBlocks(blocks, { save = true } = {}) {
    if (!Array.isArray(blocks) || blocks.length === 0) return;

    const row = document.createElement('div');
    row.className = 'message-row message bot message-blocks-row';

    const spacer = document.createElement('div');
    spacer.className = 'avatar';
    spacer.setAttribute('aria-hidden', 'true');

    const container = document.createElement('div');
    container.className = 'message-blocks-container';

    row.appendChild(spacer);
    row.appendChild(container);
    chatMessages.appendChild(row);

    renderBlocks(blocks, container, { t });
    chatMessages.scrollTop = chatMessages.scrollHeight;

    if (save) {
      historyManager.addMessage({
        type: 'bot',
        kind: 'blocks',
        blocks,
        timestamp: new Date().toISOString()
      });
    }
  }

  function displayLeadForm({ form, intro, botId, onSubmit, onStart }) {
    removeTyping();
    markPendingUserMessagesAsSeen();

    const { row, bubble } = makeBotRow();
    bubble.classList.add('bubble-form');
    chatMessages.appendChild(row);

    if (intro) {
      const introEl = document.createElement('div');
      introEl.className = 'chatyx-lead-form__intro';
      introEl.innerHTML = SecurityHelper.sanitizeHTML(formatMessageContent(intro));
      bubble.appendChild(introEl);
    }

    const handle = renderLeadForm({
      schema: form,
      container: bubble,
      t,
      botId,
      onSubmit,
      onStart
    });

    const botTimestamp = new Date().toISOString();
    appendMeta(botTimestamp);
    chatMessages.scrollTop = chatMessages.scrollHeight;

    incrementUnread();
    return handle;
  }

  function displaySubmittedSummaryAsUser(summary) {
    const row = document.createElement('div');
    row.className = 'message-row message user message-fade-in';

    const avatar = document.createElement('div');
    avatar.className = 'avatar';
    avatar.innerHTML = `<img src="${config.userAvatar}" alt="">`;

    const bubble = document.createElement('div');
    bubble.className = 'bubble';
    bubble.textContent = summary;

    row.appendChild(avatar);
    row.appendChild(bubble);
    chatMessages.appendChild(row);

    const timestamp = new Date().toISOString();
    const metaEl = createMessageMeta('user', timestamp, true);
    if (metaEl) chatMessages.appendChild(metaEl);
    chatMessages.scrollTop = chatMessages.scrollHeight;

    historyManager.addMessage({ type: 'user', content: summary, timestamp, seen: true, kind: 'lead_summary' });
  }

  return {
    displayBotResponseWithAnimation,
    displayBotResponseStreaming,
    displayBlocks,
    displayLeadForm,
    displaySubmittedSummaryAsUser
  };
}
